// src/admin/pages/cost/cost.ts

export enum CostCategory {
  RENT = 'rent',
  UTILITIES = 'utilities',
  SALARIES = 'salaries',
  MARKETING = 'marketing',
  SHIPPING_LOGISTICS = 'shipping_logistics',
  SOFTWARE_SUBSCRIPTIONS = 'software_subscriptions',
  MAINTENANCE = 'maintenance',
  INSURANCE = 'insurance',
  TAXES_LICENSES = 'taxes_licenses',
  OFFICE_SUPPLIES = 'office_supplies',
  PROFESSIONAL_SERVICES = 'professional_services',
  OTHER = 'other',
}

export enum CostType {
  ONE_TIME = 'one_time',
  RECURRING = 'recurring',
}

export enum RecurrenceFrequency {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

export enum CostStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  ENDED = 'ended',
}

export interface Cost {
  id?: string;
  title: string;
  category: CostCategory;
  costType: CostType;
  amount: number;
  // For one-time costs: the date it was incurred.
  // For recurring costs: the date the recurrence started.
  incurredDate: Date;
  // Required when costType === RECURRING
  recurrenceFrequency?: RecurrenceFrequency;
  // Optional — when a recurring cost is scheduled to stop (e.g. a contract end date)
  endDate?: Date | null;
  status: CostStatus;
  vendor?: string;
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  createdByName?: string;
}

export interface CostFormData {
  title: string;
  category: CostCategory;
  costType: CostType;
  amount: string;
  incurredDate: string; // yyyy-mm-dd
  recurrenceFrequency: RecurrenceFrequency | '';
  endDate: string; // yyyy-mm-dd or ''
  status: CostStatus;
  vendor: string;
  notes: string;
}

export interface CostStats {
  totalCount: number;
  oneTimeCount: number;
  recurringCount: number;
  activeCount: number;
  pausedCount: number;
  endedCount: number;
  totalAmountAllTime: number;
  totalAmountThisMonth: number;
}

export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
  [CostCategory.RENT]: 'Rent',
  [CostCategory.UTILITIES]: 'Utilities',
  [CostCategory.SALARIES]: 'Salaries & Wages',
  [CostCategory.MARKETING]: 'Marketing & Advertising',
  [CostCategory.SHIPPING_LOGISTICS]: 'Shipping & Logistics',
  [CostCategory.SOFTWARE_SUBSCRIPTIONS]: 'Software & Subscriptions',
  [CostCategory.MAINTENANCE]: 'Maintenance & Repairs',
  [CostCategory.INSURANCE]: 'Insurance',
  [CostCategory.TAXES_LICENSES]: 'Taxes & Licenses',
  [CostCategory.OFFICE_SUPPLIES]: 'Office Supplies',
  [CostCategory.PROFESSIONAL_SERVICES]: 'Professional Services',
  [CostCategory.OTHER]: 'Other',
};

export const COST_TYPE_LABELS: Record<CostType, string> = {
  [CostType.ONE_TIME]: 'One-Time',
  [CostType.RECURRING]: 'Recurring',
};

export const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  [RecurrenceFrequency.WEEKLY]: 'Weekly',
  [RecurrenceFrequency.MONTHLY]: 'Monthly',
  [RecurrenceFrequency.QUARTERLY]: 'Quarterly',
  [RecurrenceFrequency.YEARLY]: 'Yearly',
};

export const COST_STATUS_COLORS: Record<CostStatus, string> = {
  [CostStatus.ACTIVE]: 'success',
  [CostStatus.PAUSED]: 'warning',
  [CostStatus.ENDED]: 'default',
};

// Approximate number of occurrences per year for each recurrence frequency —
// used to annualize recurring costs for at-a-glance stats.
export const OCCURRENCES_PER_YEAR: Record<RecurrenceFrequency, number> = {
  [RecurrenceFrequency.WEEKLY]: 52,
  [RecurrenceFrequency.MONTHLY]: 12,
  [RecurrenceFrequency.QUARTERLY]: 4,
  [RecurrenceFrequency.YEARLY]: 1,
};
